<template>
  <main v-if="address && Object.keys(address).length > 0" class="flex flex-col flex-1 px-4 py-8 md:px-10 lg:px-20 max-w-[1440px] mx-auto w-full">
    <!-- Results Dashboard -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <!-- Left Column: Data & Details -->
      <div class="lg:col-span-7 flex flex-col gap-6">
        <!-- Status Card -->
        <div class="flex items-center justify-between p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
          <div class="flex items-center gap-3">
            <div class="size-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p class="text-xs text-slate-500 uppercase font-semibold tracking-wider">Status</p>
              <p class="text-slate-900 font-bold">Success</p>
            </div>
          </div>
          <div class="text-right" v-if="address.structured?.confidence !== undefined">
            <p class="text-xs text-slate-500 uppercase font-semibold tracking-wider">Confidence</p>
            <p class="text-slate-900 font-bold">{{ confidenceLevel }} ({{ address.structured.confidence.toFixed(2) }})</p>
          </div>
        </div>

        <!-- Structured Data Card -->
        <div class="rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div class="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              <h3 class="text-slate-900 font-bold text-lg">Structured Data</h3>
            </div>
          </div>
          <div class="p-0">
            <div v-if="address.sla" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Full Address</div>
              <div class="p-4 text-slate-900 text-sm font-medium">{{ address.sla }}</div>
            </div>

            <div v-if="address.structured?.number?.number" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Street Number</div>
              <div class="p-4 text-slate-900 text-sm font-medium">{{ address.structured.number.number }}</div>
            </div>

            <div v-if="address.structured?.street" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Street</div>
              <div class="p-4 text-slate-900 text-sm font-medium">
                {{ address.structured.street.name }} {{ address.structured.street.type?.name || '' }}
              </div>
            </div>

            <div v-if="address.structured?.locality?.name" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Locality</div>
              <div class="p-4 text-slate-900 text-sm font-medium">{{ address.structured.locality.name }}</div>
            </div>

            <div v-if="address.structured?.state" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">State</div>
              <div class="p-4 text-slate-900 text-sm font-medium">{{ address.structured.state.abbreviation }}</div>
            </div>

            <div v-if="address.structured?.postcode" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Postcode</div>
              <div class="p-4 text-slate-900 text-sm font-medium">{{ address.structured.postcode }}</div>
            </div>

            <div class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Country</div>
              <div class="p-4 text-slate-900 text-sm font-medium">Australia</div>
            </div>

            <div v-if="mapCenter" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Coordinates</div>
              <div class="p-4 text-blue-600 text-sm font-mono bg-blue-50/50">
                {{ mapCenter[0].toFixed(4) }}, {{ mapCenter[1].toFixed(4) }}
              </div>
            </div>

            <div v-if="address.geocoding?.level" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Match Level</div>
              <div class="p-4 text-slate-900 text-sm font-medium flex items-center gap-2">
                <span class="size-2 rounded-full bg-blue-600"></span>
                {{ address.geocoding.level.name }}
              </div>
            </div>

            <div v-if="address.pid" class="grid grid-cols-[120px_1fr] border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <div class="p-4 text-slate-500 text-sm font-medium bg-slate-50/30">Address ID</div>
              <div class="p-4 text-slate-700 text-xs font-mono">{{ address.pid }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Map Visualization -->
      <div class="lg:col-span-5 flex flex-col h-full min-h-[500px]">
        <div class="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden h-full flex flex-col">
          <div class="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <h3 class="text-slate-900 font-bold text-lg">Map Visualization</h3>
            </div>
          </div>
          <div class="relative w-full h-full bg-slate-100">
            <l-map
              v-if="mapCenter"
              :zoom="zoom"
              :center="mapCenter"
              :use-global-leaflet="false"
              class="h-full w-full"
            >
              <l-tile-layer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                layer-type="base"
                name="OpenStreetMap"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              ></l-tile-layer>
              <l-marker :lat-lng="mapCenter">
                <l-popup>
                  <div class="text-sm">
                    <p class="font-semibold">{{ address.sla }}</p>
                    <p class="text-xs text-gray-600 mt-1">
                      {{ mapCenter[0].toFixed(6) }}, {{ mapCenter[1].toFixed(6) }}
                    </p>
                  </div>
                </l-popup>
              </l-marker>
            </l-map>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import "leaflet/dist/leaflet.css";
import { LMap, LTileLayer, LMarker, LPopup } from "@vue-leaflet/vue-leaflet";

export default {
  name: 'AddressDetails',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
  },
  props: {
    address: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      zoom: 17
    }
  },
  computed: {
    mapCenter() {
      if (this.address?.geocoding?.geocodes && this.address.geocoding.geocodes.length > 0) {
        const defaultGeocode = this.address.geocoding.geocodes.find(g => g.default) || this.address.geocoding.geocodes[0];
        return [defaultGeocode.latitude, defaultGeocode.longitude];
      }
      return null;
    },
    confidenceLevel() {
      const confidence = this.address?.structured?.confidence;
      if (!confidence) return 'Unknown';
      if (confidence >= 0.9) return 'High';
      if (confidence >= 0.7) return 'Medium';
      return 'Low';
    }
  }
}
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
